import { FC, useEffect, useState } from "react";
import { fetchAsync, postAsync } from "../../services/ApiHandler";
import { Account } from "../../model/Account";
import { removeToken } from "../../services/CookieHandler";
import { Switch } from "@mantine/core";
import toast, { Toaster } from "react-hot-toast";
import { Helmet } from "react-helmet";
import { isLoggedIn } from "../../services/CookieHandler";
import { useHeaderStore } from "../../parts/CommonHeader";
import { getProfileImageUrl } from "../../services/ProfileImageUtil";
import { FooterMenu } from "../../components/FooterMenu";
import { getExpiryText, getPlanNameBySubscription, getPlanPriceText, getRnewwalType, getTransactionItemName } from "../../services/SubscriptionService";
import { useNavigate } from "react-router-dom";
import { StripeTransaction } from "../../model/StripeTransaction";
import dayjs from "dayjs";
import { StripePortalResponse } from "../../model/StripePortalResponse";

export const ProfilePage: FC = () => {
  const [adChecked, setAdChecked] = useState(true);
  const [noticeChecked, setNoticeChecked] = useState(true);
  const [transactions, setTransactions] = useState<StripeTransaction[]>([]);
  const [isPaymentHistoryOpen, setIsPaymentHistoryOpen] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const { profileImage, setProfileImage, account, fetchAccount, subscription, remainCount } = useHeaderStore();
  const navigate = useNavigate();

  useEffect(() => {
    if (!isLoggedIn()) return;
    const fetchTransactions = async () => {
      const response = await fetchAsync<StripeTransaction[]>('Stripe/GetTransactions');
      setTransactions(response.slice(0, 5)); // 最新5件のみ表示
    };
    fetchTransactions();
  }, [])

  function logout() {
    removeToken();
    window.location.href = '/image-generation'
  }

  async function switchAdChecked(checked: boolean) {
    setAdChecked(checked);
    var param = { isActive: checked ? 1 : 0 };
    const response = await postAsync<string>("Account/SwitchAdFlag", param);
    if(response.code !== 1) {
      toast.error(response.message ?? "");
    }
    fetchAccount();
  }

  async function switchNoticeChecked(checked: boolean) {
    setNoticeChecked(checked);
    var param = { isActive: checked ? 1 : 0 };
    await postAsync<string>("Account/SwitchMailFlag", param);
    fetchAccount();
  }

  async function openStripePortal() {
    setIsProcessing(true);
    if (!isLoggedIn()) { setIsProcessing(false); return; }
    const response = await fetchAsync<StripePortalResponse>('Stripe/GetPortalSessionUrl');
    window.location.href = response.url;
    setIsProcessing(false);
  }

  return <>
    <Helmet>
      <title>マイページ - Drop AI</title>
      <meta name="description" content="マイページでお客さまのアカウント情報を確認いただけます。" />
      <meta property="og:url" content="https://drop4i.com/account" />
      <meta property="og:type" content="website" />
      <meta property="og:title" content="マイページ - Drop AI | 無料で使えるAI画像生成ツール" />
      <meta property="og:description" content="マイページでお客さまのアカウント情報を確認いただけます。" />
    </Helmet>
    <Toaster />
      <div className="min-h-[calc(100vh-76px)] sm:bg-[#eaeaea80] pb-16 sm:pb-0">
        <div className="pt-1"></div>
        <div className="mt-2 sm:mt-10 mx-auto max-w-[800px] bg-white rounded-lg py-4 px-6"> 
          <div className="text-2xl font-bold mb-4">アカウント</div>
          <div className="flex mb-4">
              <img src={getProfileImageUrl(profileImage)} className="h-[100px] w-[100px] rounded-full" />
          </div>
          <div className="mb-4 items-center block sm:flex">
            <span className="flex items-center w-[180px]">
              <img src="/images/icon/icon-user.svg" className="w-[20px] h-[20px] mr-2" />
              <span className="">User ID</span>
            </span>
            <span className="text-sm">{account?.accountId}</span>
          </div>
          <div className="mb-4 items-center block sm:flex">
            <span className="flex items-center w-[180px]">
              <img src="/images/icon/icon-email.svg" className="w-[20px] h-[20px] mr-2" />
              <span className="">メールアドレス</span>
            </span>
            <span className="text-sm">{account?.email}</span>
          </div>
          <div className="flex mb-4 items-center">
            <img src="/images/icon/icon-ad.svg" className="w-[20px] h-[20px] mr-2" />
            <span className="w-[150px]">広告表示</span>
            <Switch
              color="#0909FF"
              checked={false}
              //onChange={(event) => switchAdChecked(event.currentTarget.checked)}
            />
          </div>
          <div className="flex mb-4 items-center">
            <img src="/images/icon/icon-bell.svg" className="w-[20px] h-[20px] mr-2" />
            <span className="w-[150px]">お知らせメール</span>
            <Switch
              color="#0909FF"
              checked={noticeChecked}
              onChange={(event) => switchNoticeChecked(event.currentTarget.checked)}
            />
          </div>
        </div>
        
        <div className="sm:hidden mt-6 bg-white rounded-lg border border-gray-200 p-4">
          <div className="text-sm text-gray-600 mb-2">現在のプラン</div>
          <div className="flex items-center justify-between mb-4">
            <div className="text-2xl font-bold">{getPlanNameBySubscription(subscription)}</div>
            <div className="text-xl">{getPlanPriceText(subscription)}</div>
          </div>
          <div className="flex items-center text-sm text-gray-600 mb-3">
            <span>契約種別</span>
            <span className="ml-auto">{getRnewwalType(subscription)}</span>
          </div>
          <div className="flex items-center text-sm text-gray-600 mb-4">
            <span>プランの有効期限</span>
            <span className="ml-auto">{getExpiryText(subscription)}</span>
          </div>
          <div className="flex items-center text-sm text-gray-600 mb-4">
            <span>お客様の決済情報</span>
            <span className="ml-auto">{subscription?.isAppSubscription ? 'App Store決済' : 'Stripe決済'}</span>
          </div>
          <button 
            onClick={() => navigate('/plan')}
            className="w-full gradation-button text-white py-3 rounded-lg font-bold"
          >
            プランをアップグレードする
          </button>
        </div>

        <div className="sm:hidden mt-4 bg-white rounded-lg border border-gray-200 p-4">
          <div className="text-sm text-gray-600 mb-2">現在のクレジット</div>
          <div className="text-3xl font-bold mb-4">{remainCount || 0}</div>
          <button 
            onClick={() => navigate('/plan')}
            className="w-full gradation-border-button py-3 rounded-lg font-bold flex items-center justify-center"
          >
            <span className="mr-1 gradation-text">+</span>
            <span className="gradation-text">クレジットを追加</span>
          </button>
        </div>

        <div className="sm:hidden mt-4 bg-white rounded-lg">
          <button
            onClick={() => setIsPaymentHistoryOpen(!isPaymentHistoryOpen)}
            className="w-full py-3 flex items-center justify-center"
          >
            <span className="text-sm text-gray-600">お支払いの詳細を{isPaymentHistoryOpen ? '閉じる' : '確認する'}</span>
            <span className={`ml-2 text-gray-600 transition-transform duration-200 ${isPaymentHistoryOpen ? 'rotate-180' : ''}`}>
              ▼
            </span>
          </button>
          
          {isPaymentHistoryOpen && (
            <div className="px-4 pb-4">
              <div className="text-sm font-bold mt-4 mb-3">お支払い方法</div>
              <button 
                disabled={isProcessing} 
                onClick={() => openStripePortal()} 
                className="w-full border border-gray-300 rounded py-2 px-4 text-sm disabled:opacity-50 mb-6"
              >
                お支払いの詳細を確認する
              </button>
              
              <div className="text-sm font-bold mb-3">お支払い履歴</div>
              { transactions.length === 0 ? (
                <div className="text-sm text-gray-600">お支払い履歴はありません。</div>
              ) : (
                <div className="space-y-3">
                  <div className="flex text-xs text-gray-600 border-b pb-2">
                    <div className="w-24">日付</div>
                    <div className="flex-1">説明</div>
                    <div className="text-right">合計</div>
                  </div>
                  {transactions.map((transaction, index) => (
                    <div key={index} className="flex text-sm border-b pb-2">
                      <div className="w-24">{dayjs(transaction.createdDate).format('YYYY-M-D')}</div>
                      <div className="flex-1 text-xs">{getTransactionItemName(transaction.productName, transaction.amount)}</div>
                      <div className="text-right">{transaction.amount.toLocaleString()} 円</div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    <FooterMenu />
  </>;
}
  
